import React, { useEffect } from 'react';
import {
  Drawer,
  Box,
  Typography,
  Button,
  FormHelperText,
  CircularProgress,
  FormControl,
  InputLabel,
  IconButton,
} from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import FileUploadContainer from '../../../Components/FileUpload/FileUploadContainer';
import { AddFiletoFolder, UpdateFileInFolder } from '../../../api/services';
import CustomInput from '../../../Components/Inputs/CustomInput';
import CloseIcon from '@mui/icons-material/Close';

interface AddFileProps {
  open: boolean;
  onClose: () => void;
  editingFile?: FileData | null;
  folderId: string | undefined;
}


interface FileData {
  _id: string;
  name: string;
  size: number;
  url: string;
  fileType: string;
  parent : string;
  metadata: {
    websiteurl: string;
  };
}

const AddFileDrawer: React.FC<AddFileProps> = ({ open, onClose, editingFile, folderId }) => {
    const queryClient = useQueryClient();
  
  const {
    control,
    handleSubmit,
    setValue,
    reset,
    formState: { errors },
  } = useForm<FileData>({
    defaultValues: {
      name: editingFile?.name || '',
      size: editingFile?.size || 0,
      url: editingFile?.url || '',
      fileType: editingFile?.fileType || '',
      parent: folderId || '',
      metadata: {
        websiteurl: editingFile?.metadata.websiteurl || '',
      },
    },
  });

  useEffect(() => {
    console.log(editingFile, "temp101");
  },  [editingFile ])

  // Update form state if editingFile is available
  useEffect(() => {
    if (editingFile) {
      reset({
        name: editingFile.name,
        size: editingFile.size,
        url: editingFile.url,
        fileType: editingFile.fileType,
        parent: folderId || '',
        metadata: {
          websiteurl: editingFile.metadata.websiteurl,
        },
      });
    } else {
      reset({
        name: '',
        size: 0,
        url: '',
        fileType: '',
        parent: folderId || '',
        metadata: {
          websiteurl: '',
        },
      });
    }
  }, [editingFile, reset]);

  // Add file mutation
  const addMutation = useMutation((data: FileData) => AddFiletoFolder(data), {
    onSuccess: () => {
      toast.success('File added successfully');
      queryClient.invalidateQueries('folderData'); 
      onClose();
    },
    onError: (error: any) => {
      console.error('Error adding file:', error);
      toast.error('Error adding file. Please try again.');
    },
  });



  // Update file mutation
  const updateMutation = useMutation(
    (data: FileData) => UpdateFileInFolder(editingFile?._id || '', data),
    {
      onSuccess: () => {
        toast.success('File updated successfully');
        queryClient.invalidateQueries('folderData'); // Ensure the query is invalidated
        onClose(); // Close the drawer on success
      },
      onError: (error: any) => {
        console.error('Error updating file:', error);
        toast.error('Error updating file. Please try again.');
      },
    }
  );

  // Handle form submission
  const onSubmit = (data: FileData) => {
    if (!data.url || !data.name || !data.fileType) {
      toast.error('All fields are required.');
      return;
    }

    if (editingFile) {
      updateMutation.mutate(data);
    } else {
      addMutation.mutate(data);
    }
  };

  // Handle file selection
  const handleFileSelect = (file: { url: string; name: string; size: number; type: string } | null) => {
    if (file) {
      setValue('url', file.url);
      setValue('size', file.size);
      setValue('name', file.name);
      if (file.type.startsWith('image')) {
        setValue('fileType', 'image');
      } else if (file.type.startsWith('video')) {
        setValue('fileType', 'video');
      } else if (file.type.startsWith('application')) {
        setValue('fileType', 'document');
      }

    } else {
      handleFileDelete();
    }
  };

  // Handle file deletion
  const handleFileDelete = () => {
    setValue('url', '');
    setValue('size', 0);
    setValue('name', '');
    setValue('fileType', '');
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { width: '100%', maxWidth: '400px',background:'white' },
      }}
    >
        <Box
          sx={{
            width: '100%',
            height: 60,
            // marginTop: "64px",
            backgroundColor: '#0f63a5',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '0 16px',
            color: 'white',
            zIndex: (theme) => theme.zIndex.drawer + 10,
          }}
            >
              <Typography variant="h6">{editingFile ? 'Edit File' : 'Add File'}</Typography>
              <IconButton onClick={onClose} sx={{ color: 'white' }}>
                <CloseIcon />
              </IconButton>
            </Box>
      <Box sx={{ padding: '20px' ,}}>
        {/* <Typography variant="h6">{editingFile ? 'Edit File' : 'Add File'}</Typography> */}
        <form onSubmit={handleSubmit(onSubmit)}>
          {/* parent */}
          <Controller
            name="parent"
            control={control}
            render={({ field }) => <input type="hidden" {...field} />}
          />


          {/* File upload section */}
          <Controller
            name="url"
            control={control}
            render={({ field }) => (
              <FileUploadContainer
                onFileSelect={handleFileSelect}
                foldername={`Storage/${folderId}`}
                existingImage={editingFile?.url}
                type={editingFile?.fileType}
                onDelete={handleFileDelete}
              />
            )}
          />
          <FormHelperText error>{errors.url?.message}</FormHelperText>

          <Box sx={{ height: 20 }} />

          {/* File Name input */}
          <FormControl error={!!errors.name} sx={{ mb: 2 , width: '100%' }}>
            <InputLabel shrink htmlFor="name" sx={{ left: '-12px !important',color:'black' }}>
              File Name
            </InputLabel>
            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <CustomInput
                  {...field}
                  placeholder="File Name"
                  error={!!errors.name}
                  sx={{ width: '100%', marginBottom: 2 ,color:'black'}}
                />
              )}
            />
            <FormHelperText error>{errors.name?.message}</FormHelperText>
          </FormControl>

          {/* File Type input */}
          <FormControl sx={{ mb: 2 , width: '100%' }}>
            <InputLabel shrink htmlFor="fileType" sx={{ left: '-12px !important' }}>
              File Type
            </InputLabel>
            <Controller
              name="fileType"
              control={control}
              render={({ field }) => (
                <CustomInput
                  {...field}
                  placeholder="File Type"
                  disabled
                  sx={{ width: '100%', marginBottom: 2 ,color:'black'}}
                />
              )}
            />
            <FormHelperText error>{errors.fileType?.message}</FormHelperText>
          </FormControl>


            {control._formValues.fileType === 'image' && (
            <FormControl sx={{ mb: 2, width: '100%' }}>
              <InputLabel shrink htmlFor="websiteurl" sx={{ left: '-12px !important',color:'black' }}>
              Website URL
              </InputLabel>
              <Controller
              name="metadata.websiteurl"
              control={control}
              render={({ field }) => (
                <CustomInput
                {...field}
                placeholder="Website URL"
                sx={{ width: '100%', marginBottom: 2 }}
                />
              )}
              />
            <FormHelperText error>{errors.metadata?.websiteurl?.message}</FormHelperText>
            </FormControl>
            )}

          {/* Action buttons */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
            <Button variant="outlined" color="error" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="contained"
              type="submit"
              disabled={addMutation.isLoading || updateMutation.isLoading}
              startIcon={addMutation.isLoading || updateMutation.isLoading ? <CircularProgress size={20} /> : null}
            >
              {editingFile ? 'Update File' : 'Add File'}
            </Button>
          </Box>
        </form>
      </Box>
    </Drawer>
  );
};

export default AddFileDrawer;
